import React, { useContext, useEffect, useState } from 'react';
import { I18nContext } from '../../context/translations/TranslationsContext.context';
import { PensionPlanTransfer } from '../../api/pension-plan-transfers/interfaces/PensionPlanTransfer';
import { TransferStatusCard } from '../ui/transfer-status-card';
import {
	OpcionesTarjetaTraspaso,
	ocultarConfirmado,
	transferToCardProps
} from '../../utils/traspasos/tarjetasDeTraspaso';
import {
	hasSeenTransferConfirmation,
	markTransferConfirmationSeen
} from '../../utils/transferConfirmationSeen';

export interface TarjetaTraspasoProps extends Omit<OpcionesTarjetaTraspaso, 'kycDone'> {
	transfer: PensionPlanTransfer;
	kycDone: boolean;
	/** El detalle que se despliega (timeline, ficha). Ignorado con `directAccessCta`. */
	children?: React.ReactNode;
	defaultExpanded?: boolean;
	/** Se salta el «se ve una sola vez» del completado (panel de depuración, listas completas). */
	alwaysShowConfirmed?: boolean;
	/** Card puente a OTRO plan del usuario: no se despliega y lleva un botón de acceso directo. */
	directAccessCta?: { label: string; onClick: () => void };
}

/**
 * Un traspaso en la tarjeta de Proa (`ui/transfer-status-card`). Lo que dice la tarjeta lo decide
 * `transferToCardProps`; aquí solo se traduce y se aplica el «el completado se ve una sola vez»,
 * que necesita `localStorage` y el montaje (chirigota#586).
 */
const TarjetaTraspaso: React.FC<TarjetaTraspasoProps> = ({
	transfer,
	kycDone,
	children,
	defaultExpanded = true,
	alwaysShowConfirmed = false,
	directAccessCta,
	pendingReason,
	originPreposition
}) => {
	const {
		state: { translate }
	} = useContext(I18nContext);
	const props = transferToCardProps(
		transfer,
		{ kycDone, pendingReason, originPreposition },
		translate
	);
	const esCompletado = props.variant === 'completed';
	const [yaVisto] = useState(() =>
		esCompletado ? hasSeenTransferConfirmation(transfer.id) : false
	);
	useEffect(() => {
		if (esCompletado && !yaVisto) markTransferConfirmationSeen(transfer.id);
		// Deliberadamente solo al montar: ESTE montaje sigue enseñando el completado; el siguiente
		// ya lo encuentra marcado en `localStorage` y no lo pinta.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (ocultarConfirmado(props.variant, yaVisto, alwaysShowConfirmed)) return null;

	return (
		<div data-testid="transfer-status-card">
			<TransferStatusCard
				{...props}
				defaultExpanded={defaultExpanded}
				directAccessCta={directAccessCta}
			>
				{children}
			</TransferStatusCard>
		</div>
	);
};

export default TarjetaTraspaso;
